import { useCallback, useEffect, useState } from "react";
import { Calendar, Clock, MapPin, Users, Sparkles, Wallet } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const REFRESH_MS = 15000;

export default function EventDetails() {
  const [event, setEvent] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/api/event`, { cache: "no-store" });
      if (r.ok) {
        const d = await r.json();
        setEvent(d);
      }
    } catch (_e) {
      /* swallow network errors — component keeps last value */
    }
  }, []);

  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, REFRESH_MS);
    const onCreated = () => refresh();
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    window.addEventListener("reservation-created", onCreated);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(interval);
      window.removeEventListener("reservation-created", onCreated);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refresh]);

  const remaining = event?.remaining_seats ?? 8;
  const total = event?.total_seats ?? 8;
  const isFull = event?.is_full ?? false;

  const rows = [
    { icon: Calendar, label: "Date", value: "Dimanche 10 mai 2026" },
    { icon: Clock, label: "Horaire", value: "15h00 — 17h30" },
    { icon: MapPin, label: "Lieu", value: "Chez Douceur & Bienfait" },
    { icon: Users, label: "Capacité", value: `${total} places — petit groupe` },
    { icon: Wallet, label: "Paiement", value: "À régler en magasin" },
  ];

  return (
    <div
      data-testid="event-details"
      className="bg-white border border-[var(--line)] rounded-3xl p-7 sm:p-9 h-full flex flex-col shadow-[0_2px_24px_-12px_rgba(41,37,36,0.10)]"
    >
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-[var(--accent)] font-medium">
            Atelier à l'affiche
          </p>
          <h3 className="font-serif text-3xl sm:text-4xl mt-2 leading-tight text-[var(--text-primary)]">
            Initiation &amp; pratique
            <br />
            <span className="italic">du Tarot</span>
          </h3>
        </div>
        <div className="shrink-0 w-12 h-12 rounded-full bg-[var(--bg-secondary)] text-[var(--accent)] flex items-center justify-center">
          <Sparkles size={20} />
        </div>
      </div>

      <p className="text-sm sm:text-base text-[var(--text-secondary)] leading-relaxed mb-6">
        Une après-midi pour découvrir le tarot dans une ambiance chaleureuse et
        bienveillante — initiation, tirages guidés et échanges en petit comité.
      </p>

      <ul className="space-y-4 flex-1">
        {rows.map(({ icon: Icon, label, value }) => (
          <li key={label} className="flex gap-3">
            <div className="shrink-0 w-9 h-9 rounded-full bg-[var(--bg-secondary)] flex items-center justify-center text-[var(--accent)]">
              <Icon size={16} />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-[0.16em] text-[var(--text-secondary)]/80">
                {label}
              </p>
              <p className="font-serif text-lg text-[var(--text-primary)] leading-tight">
                {value}
              </p>
            </div>
          </li>
        ))}
      </ul>

      <div className="mt-7 pt-6 border-t border-dashed border-[var(--line)] flex items-end justify-between gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-[0.16em] text-[var(--text-secondary)]/80">
            Tarif par personne
          </p>
          <p className="font-serif text-4xl text-[var(--text-primary)] leading-none mt-1">
            40<span className="text-2xl align-top">€</span>
          </p>
        </div>
        <div
          data-testid="seats-availability"
          className={`px-4 py-2 rounded-full text-xs font-medium tracking-wide ${
            isFull
              ? "bg-[#fbeae7] text-[#9b3a2b]"
              : "bg-[#eaf3ec] text-[#2f6b3d]"
          }`}
        >
          {isFull
            ? "Complet"
            : `${remaining} place${remaining > 1 ? "s" : ""} restante${
                remaining > 1 ? "s" : ""
              }`}
        </div>
      </div>
    </div>
  );
}
